import { useEffect, useRef, useState, type FormEvent } from "react";
import { Camera, X } from "lucide-react";
import { supabase, type Existencia, type MotivoMovimiento } from "@/lib/supabase";
import { useAlmacen } from "@/lib/almacen";
import { useAuth } from "@/auth/AuthProvider";
import { useRpc, rpc } from "@/lib/useRpc";
import { cantidad, num, MOTIVOS_BAJA, MOTIVOS_ENTRADA } from "@/lib/formato";
import { Tarjeta, Boton, Campo, Input, Select, Textarea, Badge, Dialogo, Esqueleto, tonoEstado } from "@/components/ui";
import { useToast } from "@/components/ui/Toast";
import { BuscadorSku } from "./BuscadorSku";
import { comprimirFoto } from "./foto";
import { mapearErrorSupabase } from "@/lib/errores";

type Tipo = "entrada" | "baja" | "transferencia";

/** Formulario único para Entradas, Bajas y Traspasos (la app de Zepe tenía dos copias al 85% iguales). */
function FormMovimiento({ tipo }: { tipo: Tipo }) {
  const { almacen, almacenes, multi } = useAlmacen();
  const { perfil, esAdmin } = useAuth();
  const toast = useToast();
  const [prod, setProd] = useState<Existencia | null>(null);
  const [cant, setCant] = useState("");
  const [motivo, setMotivo] = useState<MotivoMovimiento>(tipo === "entrada" ? "recepcion" : "merma");
  const [referencia, setReferencia] = useState("");
  const [nota, setNota] = useState("");
  const [destino, setDestino] = useState("");
  const [foto, setFoto] = useState<File | null>(null);
  const [fotoPreview, setFotoPreview] = useState<string | null>(null);
  const [insuficiente, setInsuficiente] = useState<{ disponible: number } | null>(null);
  const [fotoSubida, setFotoSubida] = useState<string | null>(null);
  const [fisicoReal, setFisicoReal] = useState("");
  const [subiendo, setSubiendo] = useState(false);
  const cantRef = useRef<HTMLInputElement>(null);
  const modoManual = perfil?.empresa.modo_ventas === "manual";

  const registrar = useRpc("registrar_movimiento", {
    silencioso: true,
    onSuccess: () => {
      toast.exito(tipo === "entrada" ? `Entrada registrada: ${prod?.sku}` : tipo === "baja" ? `Baja registrada: ${prod?.sku}` : `Traspaso registrado: ${prod?.sku}`);
      limpiar();
    },
    onError: (e) => {
      if (e.codigo === "STOCK_INSUFICIENTE") setInsuficiente({ disponible: Number(e.detalle?.disponible ?? 0) });
      else toast.error(e.mensaje);
    },
  });

  useEffect(() => { if (prod) cantRef.current?.focus(); }, [prod]);
  useEffect(() => { if (!foto) { setFotoPreview(null); return; } const u = URL.createObjectURL(foto); setFotoPreview(u); return () => URL.revokeObjectURL(u); }, [foto]);

  function limpiar() {
    setProd(null); setCant(""); setReferencia(""); setNota(""); setFoto(null); setInsuficiente(null); setFisicoReal(""); setFotoSubida(null);
  }

  const n = Number(cant.replace(",", "."));
  const valido = !!prod && Number.isFinite(n) && n > 0 && (tipo !== "transferencia" || (destino && destino !== almacen?.id));
  const proyeccion = prod && Number.isFinite(n) ? (tipo === "entrada" ? Number(prod.cantidad_fisica) + n : Number(prod.cantidad_fisica) - n) : null;

  async function enviar(e: FormEvent) {
    e.preventDefault();
    if (!valido || !prod || !almacen) return;
    let fotoPath: string | null = fotoSubida;
    if (foto && tipo === "baja" && !fotoSubida) {
      setSubiendo(true);
      try {
        const blob = await comprimirFoto(foto);
        const path = `${perfil!.empresa.id}/${crypto.randomUUID()}.jpg`;
        const { error } = await supabase.storage.from("bajas").upload(path, blob, { contentType: "image/jpeg" });
        if (error) throw error;
        fotoPath = path;
        setFotoSubida(path);
      } catch (err) {
        const ea = mapearErrorSupabase(err);
        const seguir = window.confirm(`No se pudo subir la foto (${ea.mensaje}). ¿Registrar la baja sin foto?`);
        if (!seguir) { setSubiendo(false); return; }
      }
      setSubiendo(false);
    }
    registrar.mutate({
      p_producto_id: prod.producto_id!, p_almacen_id: almacen.id, p_tipo: tipo,
      p_motivo: tipo === "transferencia" ? "traspaso_salida" : motivo, p_cantidad: n,
      p_referencia: referencia || null, p_nota: nota || null, p_foto_path: fotoPath,
      p_almacen_destino_id: tipo === "transferencia" ? destino : null,
    } as never);
  }

  async function contarYReintentar() {
    if (!prod || !almacen) return;
    const f = Number(fisicoReal.replace(",", "."));
    if (!Number.isFinite(f) || f < 0) return;
    try {
      await rpc("registrar_conteo", { p_producto_id: prod.producto_id!, p_almacen_id: almacen.id, p_zona_id: prod.zona_id ?? undefined, p_cantidad_fisica: f });
      setProd({ ...prod, cantidad_fisica: f });
      setInsuficiente(null);
      if (f >= n) {
        registrar.mutate({
          p_producto_id: prod.producto_id!, p_almacen_id: almacen.id, p_tipo: tipo,
          p_motivo: tipo === "transferencia" ? "traspaso_salida" : motivo, p_cantidad: n,
          p_referencia: referencia || null, p_nota: nota || null, p_foto_path: fotoSubida,
          p_almacen_destino_id: tipo === "transferencia" ? destino : null,
        } as never);
      } else {
        toast.info(`Conteo guardado: ${num(f)}. Aún no alcanza para ${num(n)}.`);
      }
    } catch (err) {
      toast.error(mapearErrorSupabase(err).mensaje);
    }
  }

  if (!almacen) return <Esqueleto />;
  const titulo = tipo === "entrada" ? "Entradas" : tipo === "baja" ? "Bajas" : "Traspasos";
  const sub = tipo === "entrada" ? "Mercancía que llega y acomodas." : tipo === "baja" ? "Lo que sale sin pasar por caja: merma, daño, muestras." : "Mover producto de una bodega a otra.";
  const motivos = tipo === "entrada" ? MOTIVOS_ENTRADA : MOTIVOS_BAJA.filter((m) => !m.soloManual || modoManual);

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div><h1 className="text-2xl font-bold">{titulo}</h1><p className="text-sm text-tinta-suave">{sub}{multi ? ` · ${almacen.nombre}` : ""}</p></div>
      <div className="grid gap-4 md:grid-cols-5">
        <form onSubmit={enviar} className="space-y-4 md:col-span-3">
          <Tarjeta>
            <div className="space-y-4">
              <Campo etiqueta="Producto">
                {prod ? (
                  <div className="flex items-center justify-between gap-2 rounded-md border border-primary bg-indigo-50 px-3 py-2">
                    <span><span className="font-medium">{prod.sku}</span> <span className="text-tinta-suave">{prod.nombre}</span></span>
                    <button type="button" onClick={limpiar} className="rounded p-1 hover:bg-white" aria-label="Cambiar producto"><X className="h-4 w-4" /></button>
                  </div>
                ) : (
                  <BuscadorSku almacenId={almacen.id} onElegir={setProd} />
                )}
                {!prod && tipo === "entrada" && <PendienteCatalogo almacenId={almacen.id} esAdmin={esAdmin} />}
              </Campo>
              {tipo === "baja" && (
                <Campo etiqueta="Motivo" htmlFor="motivo">
                  <Select id="motivo" value={motivo} onChange={(e) => setMotivo(e.target.value as MotivoMovimiento)}>
                    {motivos.map((m) => <option key={m.valor} value={m.valor}>{m.etiqueta}</option>)}
                  </Select>
                </Campo>
              )}
              <Campo etiqueta={`Cantidad${prod ? ` (${prod.unidad})` : ""}`} htmlFor="cantidad">
                <Input id="cantidad" ref={cantRef} inputMode="decimal" placeholder="0" value={cant} onChange={(e) => setCant(e.target.value)} className="tabular h-14 text-2xl font-semibold" disabled={!prod} />
              </Campo>
              {tipo === "entrada" && (
                <Campo etiqueta="Motivo" htmlFor="motivo-e">
                  <Select id="motivo-e" value={motivo} onChange={(e) => setMotivo(e.target.value as MotivoMovimiento)}>
                    {motivos.map((m) => <option key={m.valor} value={m.valor}>{m.etiqueta}</option>)}
                  </Select>
                </Campo>
              )}
              {tipo === "transferencia" && (
                <Campo etiqueta="Almacén destino" htmlFor="destino">
                  <Select id="destino" value={destino} onChange={(e) => setDestino(e.target.value)} required>
                    <option value="">Elige…</option>
                    {almacenes.filter((a) => a.id !== almacen.id).map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
                  </Select>
                </Campo>
              )}
              <Campo etiqueta={tipo === "entrada" ? "Referencia (orden de compra, proveedor)" : "Referencia"} htmlFor="ref">
                <Input id="ref" value={referencia} onChange={(e) => setReferencia(e.target.value)} placeholder={tipo === "entrada" ? "Ej. OC-2026-118" : "Opcional"} />
              </Campo>
              <Campo etiqueta="Nota" htmlFor="nota">
                <Textarea id="nota" value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Opcional" className="min-h-16" />
              </Campo>
              {tipo === "baja" && (
                <Campo etiqueta="Foto (opcional)">
                  <div className="flex items-center gap-3">
                    <label className="inline-flex h-11 cursor-pointer items-center gap-2 rounded-md border border-borde bg-white px-4 hover:bg-gray-50">
                      <Camera className="h-5 w-5" /> {foto ? "Cambiar foto" : "Tomar foto"}
                      <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => setFoto(e.target.files?.[0] ?? null)} />
                    </label>
                    {fotoPreview && <img src={fotoPreview} alt="Foto de la baja" className="h-16 w-16 rounded object-cover" />}
                    {foto && <button type="button" onClick={() => setFoto(null)} className="text-sm text-tinta-suave underline">Quitar</button>}
                  </div>
                </Campo>
              )}
              <Boton type="submit" tamano="lg" className="w-full" disabled={!valido} cargando={registrar.isPending || subiendo}>
                {tipo === "entrada" ? "Registrar entrada" : tipo === "baja" ? "Registrar baja" : "Registrar traspaso"}
              </Boton>
            </div>
          </Tarjeta>
        </form>

        <aside className="md:col-span-2">
          <Tarjeta titulo="Proyección">
            {!prod ? <p className="text-sm text-tinta-suave">Elige un producto para ver cómo queda su existencia.</p> : (
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between"><dt className="text-tinta-suave">Físico actual</dt><dd className="tabular font-semibold">{cantidad(prod.cantidad_fisica, prod.unidad)}</dd></div>
                <div className="flex justify-between"><dt className="text-tinta-suave">Mínimo</dt><dd className="tabular">{num(prod.stock_minimo)}</dd></div>
                <div className="flex justify-between"><dt className="text-tinta-suave">Queda en</dt>
                  <dd className={`tabular font-semibold ${proyeccion != null && proyeccion < 0 ? "text-red-700" : ""}`}>{proyeccion == null ? "—" : cantidad(proyeccion, prod.unidad)}</dd></div>
                <div className="pt-1"><Badge tono={tonoEstado(proyeccion != null && proyeccion <= 0 ? "agotado" : proyeccion != null && proyeccion <= Number(prod.stock_minimo) && Number(prod.stock_minimo) > 0 ? "bajo" : "ok")}>
                  {proyeccion != null && proyeccion <= 0 ? "Quedaría agotado" : proyeccion != null && proyeccion <= Number(prod.stock_minimo) && Number(prod.stock_minimo) > 0 ? "Quedaría bajo mínimo" : "En orden"}</Badge></div>
                <div className="text-xs text-tinta-suave">{prod.ultimo_conteo_en ? `contado ${new Date(prod.ultimo_conteo_en).toLocaleDateString("es-MX")} por ${prod.ultimo_conteo_por_nombre ?? "alguien"}` : "sin contar todavía"}</div>
              </dl>
            )}
          </Tarjeta>
        </aside>
      </div>

      <Dialogo abierto={!!insuficiente} onCerrar={() => setInsuficiente(null)} titulo="No alcanza el físico">
        <p className="mb-3">El sistema dice que hay <strong className="tabular">{num(insuficiente?.disponible)} {prod?.unidad}</strong> de {prod?.sku} y quieres sacar <strong className="tabular">{num(n)}</strong>. Puede que el número del sistema esté mal.</p>
        <Campo etiqueta="¿Cuántos hay físicamente en el estante?" htmlFor="fisico-real">
          <Input id="fisico-real" inputMode="decimal" value={fisicoReal} onChange={(e) => setFisicoReal(e.target.value)} className="tabular h-14 text-2xl font-semibold" autoFocus />
        </Campo>
        <div className="mt-4 flex gap-2">
          <Boton onClick={contarYReintentar} disabled={fisicoReal.trim() === ""}>Guardar conteo y continuar</Boton>
          <Boton variante="secundario" onClick={() => setInsuficiente(null)}>Cancelar</Boton>
        </div>
      </Dialogo>
    </div>
  );
}

function PendienteCatalogo({ almacenId, esAdmin }: { almacenId: string; esAdmin: boolean }) {
  const [abierto, setAbierto] = useState(false);
  const [sku, setSku] = useState("");
  const [nota, setNota] = useState("");
  const toast = useToast();
  const reportar = useRpc("reportar_pendiente_catalogo", { onSuccess: () => { toast.exito("Aviso enviado al administrador."); setAbierto(false); setSku(""); setNota(""); } });
  return (
    <>
      <button type="button" onClick={() => setAbierto(true)} className="mt-1 text-sm text-tinta-suave underline">¿Llegó algo que no está en el catálogo?</button>
      <Dialogo abierto={abierto} onCerrar={() => setAbierto(false)} titulo="Producto que no está en el catálogo">
        <p className="mb-3 text-sm text-tinta-suave">{esAdmin ? "Puedes darlo de alta en Inventario. O deja el aviso aquí." : "Tú no puedes dar de alta productos; deja el aviso y el administrador lo crea."}</p>
        <div className="space-y-3">
          <Campo etiqueta="Clave o descripción" htmlFor="pend-sku"><Input id="pend-sku" value={sku} onChange={(e) => setSku(e.target.value)} placeholder="Ej. XYZ-999 o 'cinta 3M azul'" /></Campo>
          <Campo etiqueta="Nota" htmlFor="pend-nota"><Textarea id="pend-nota" value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Ej. llegaron 3 cajas" className="min-h-16" /></Campo>
          <Boton onClick={() => reportar.mutate({ p_almacen_id: almacenId, p_sku_texto: sku, p_nota: nota || undefined })} disabled={!sku.trim()} cargando={reportar.isPending}>Avisar al administrador</Boton>
        </div>
      </Dialogo>
    </>
  );
}

export function Entradas() { return <FormMovimiento tipo="entrada" />; }
export function Bajas() { return <FormMovimiento tipo="baja" />; }
export function Transferir() { return <FormMovimiento tipo="transferencia" />; }
